import { api } from './apiService';
import { getApiUrl } from '../config/environments';

const API_BASE_URL = getApiUrl();

/**
 * Investment Service
 * Handles all investment-related API calls
 */
class InvestmentService {
  // ============================================
  // INVESTMENT ACCOUNT MANAGEMENT
  // ============================================

  /**
   * Get all investment accounts for the current user
   */
  async getInvestments(includeArchived = false) {
    try {
      const response = await api.get('/investments', {
        params: { includeArchived }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching investments:', error);
      throw error;
    }
  }

  /**
   * Get investment portfolio summary
   */
  async getInvestmentSummary() {
    try {
      const response = await api.get('/investments/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching investment summary:', error);
      throw error;
    }
  }

  /**
   * Get single investment account by ID
   */
  async getInvestmentById(id) {
    try {
      const response = await api.get(`/investments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching investment:', error);
      throw error;
    }
  }

  /**
   * Create a new manual investment account
   */
  async createManualInvestment(accountData) {
    try {
      const response = await api.post('/investments/manual', accountData);
      return response.data;
    } catch (error) {
      console.error('Error creating manual investment:', error);
      throw error;
    }
  }

  /**
   * Update investment account
   */
  async updateInvestment(id, updates) {
    try {
      const response = await api.put(`/investments/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating investment:', error);
      throw error;
    }
  }

  /**
   * Delete investment account
   */
  async deleteInvestment(id) {
    try {
      const response = await api.delete(`/investments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting investment:', error);
      throw error;
    }
  }

  // ============================================
  // HOLDINGS MANAGEMENT
  // ============================================

  /**
   * Add a holding to an investment account
   */
  async addHolding(investmentId, holdingData) {
    try {
      const response = await api.post(`/investments/${investmentId}/holdings`, holdingData);
      return response.data;
    } catch (error) {
      console.error('Error adding holding:', error);
      throw error;
    }
  }

  /**
   * Update a holding in an investment account
   */
  async updateHolding(investmentId, holdingId, updates) {
    try {
      const response = await api.put(`/investments/${investmentId}/holdings/${holdingId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating holding:', error);
      throw error;
    }
  }

  /**
   * Delete a holding from an investment account
   */
  async deleteHolding(investmentId, holdingId) {
    try {
      const response = await api.delete(`/investments/${investmentId}/holdings/${holdingId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting holding:', error);
      throw error;
    }
  }

  // ============================================
  // PRICE SYNCING
  // ============================================

  /**
   * Sync prices for a single investment account
   */
  async syncPrices(investmentId) {
    try {
      const response = await api.post(`/investments/${investmentId}/sync-prices`);
      return response.data;
    } catch (error) {
      console.error('Error syncing prices:', error);
      throw error;
    }
  }

  /**
   * Sync prices for all investment accounts
   */
  async syncAllPrices() {
    try {
      const response = await api.post('/investments/sync-all-prices');
      return response.data;
    } catch (error) {
      console.error('Error syncing all prices:', error);
      throw error;
    }
  }

  // ============================================
  // TICKER SEARCH & QUOTES
  // ============================================

  /**
   * Search for ticker symbols
   */
  async searchTicker(query) {
    try {
      const response = await api.get(`/investments/search-ticker/${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      console.error('Error searching ticker:', error);
      throw error;
    }
  }

  /**
   * Get real-time quote for a ticker
   */
  async getQuote(ticker) {
    try {
      const response = await api.get(`/investments/quote/${encodeURIComponent(ticker)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching quote:', error);
      throw error;
    }
  }

  // ============================================
  // CSV IMPORT
  // ============================================

  /**
   * Preview CSV import
   */
  async previewCSV(file) {
    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await api.post('/investments/import/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error previewing CSV:', error);
      throw error;
    }
  }

  /**
   * Confirm CSV import and create investment account
   */
  async confirmCSVImport(importData) {
    try {
      const response = await api.post('/investments/import/confirm', importData);
      return response.data;
    } catch (error) {
      console.error('Error confirming CSV import:', error);
      throw error;
    }
  }

  /**
   * Download CSV template
   */
  async downloadTemplate() {
    try {
      const response = await api.get('/investments/import/template', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'investment-holdings-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Template downloaded successfully' };
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  }
}

export default new InvestmentService();
